import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { projectsData } from "../i18n/translations";
import { ArrowUpRight, Ruler } from "lucide-react";
import ProjectDetail from "./ProjectDetail";

const ease = [0.16, 1, 0.3, 1];

export default function Projects() {
  const { t, lang } = useLang();
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);

  const filtered = useMemo(
    () => (filter === "all" ? projectsData : projectsData.filter((p) => p.category === filter)),
    [filter]
  );

  const filters = [
    { k: "all", v: t.projects.filters.all },
    { k: "residential", v: t.projects.filters.residential },
    { k: "commercial", v: t.projects.filters.commercial },
    { k: "renovation", v: t.projects.filters.renovation },
  ];

  const openProject = projectsData.find((p) => p.id === openId) || null;

  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="py-24 md:py-36 bg-[#F7F7F9]"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-8 items-end mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease }}
              className="flex items-center gap-3 mb-8"
            >
              <span className="w-8 h-px bg-[#0A0A0A]" />
              <span className="font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase">
                {t.projects.overline} / 03
              </span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease }}
              className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight font-medium leading-[1.02]"
            >
              {t.projects.title}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease, delay: 0.15 }}
              className="mt-6 text-base md:text-lg font-light text-[#5C5C5C] max-w-[48ch]"
            >
              {t.projects.subtitle}
            </motion.p>
          </div>

          {/* Filters */}
          <div className="col-span-12 md:col-span-4 flex flex-wrap gap-2 md:justify-end">
            {filters.map((f) => (
              <button
                key={f.k}
                data-testid={`filter-${f.k}`}
                onClick={() => setFilter(f.k)}
                className={`px-4 py-2 text-xs font-mono tracking-[0.18em] uppercase border transition-colors ${
                  filter === f.k
                    ? "bg-[#0A0A0A] text-[#F7F7F9] border-[#0A0A0A]"
                    : "bg-transparent text-[#0A0A0A] border-[#0A0A0A]/30 hover:border-[#0A0A0A]"
                }`}
              >
                {f.v}
              </button>
            ))}
          </div>
        </div>

        {/* Tetris grid */}
        <div className="grid grid-cols-12 gap-6 md:gap-8">
          {filtered.map((p, i) => {
            const hasGallery = Array.isArray(p.gallery) && p.gallery.length > 0;
            // asymmetrical spans pattern - featured gets a full-width canvas
            const isFeatured = !!p.featured && i === 0 && filter === "all";
            const spans = [
              "col-span-12 md:col-span-7 aspect-[16/11]",
              "col-span-12 md:col-span-5 aspect-[4/5]",
              "col-span-12 md:col-span-5 aspect-[4/5]",
              "col-span-12 md:col-span-7 aspect-[16/11]",
              "col-span-12 md:col-span-8 aspect-[16/10]",
              "col-span-12 md:col-span-4 aspect-[4/5]",
            ];
            const offset = ["md:mt-0", "md:mt-20", "md:mt-0", "md:mt-0", "md:mt-0", "md:mt-24"];
            const cls = isFeatured
              ? "col-span-12 aspect-[16/9] md:aspect-[21/9]"
              : spans[i % spans.length];
            const off = isFeatured ? "md:mt-0" : offset[i % offset.length];

            return (
              <motion.article
                key={p.id}
                data-testid={`project-card-${p.id}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, ease, delay: (i % 3) * 0.08 }}
                onClick={() => hasGallery && setOpenId(p.id)}
                className={`${cls} ${off} relative group overflow-hidden bg-[#0A0A0A] ${
                  hasGallery ? "cursor-pointer" : ""
                }`}
              >
                <img
                  src={p.image}
                  alt={p.title[lang]}
                  loading="lazy"
                  className="laka-photo w-full h-full object-cover group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/75 via-[#0A0A0A]/10 to-transparent opacity-60 group-hover:opacity-100 transition-opacity duration-500" />

                {/* top meta */}
                <div className="absolute top-4 left-4 right-4 flex justify-between items-start font-mono text-[10px] tracking-[0.22em] uppercase text-[#F7F7F9]/90">
                  <span className="flex items-center gap-2">
                    N° {String(i + 1).padStart(3, "0")}
                    {isFeatured && (
                      <span className="bg-[#D35400] text-white px-2 py-0.5">
                        {lang === "fr" ? "PROJET EN VEDETTE" : "FEATURED"}
                      </span>
                    )}
                  </span>
                  <span>{p.year}</span>
                </div>

                {/* bottom meta */}
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 text-[#F7F7F9] translate-y-0 md:translate-y-3 md:group-hover:translate-y-0 transition-transform duration-500">
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#F7F7F9]/80 mb-2 flex justify-between">
                    <span>{p.scope}</span>
                    <span>{p.location}</span>
                  </div>
                  <div className="flex items-end justify-between gap-3">
                    <h3 className={`font-display tracking-tight leading-tight max-w-[80%] ${isFeatured ? "text-2xl md:text-4xl" : "text-xl md:text-2xl"}`}>
                      {p.title[lang]}
                    </h3>
                    <span className="w-10 h-10 border border-[#F7F7F9]/40 flex items-center justify-center shrink-0 group-hover:bg-[#D35400] group-hover:border-[#D35400] transition-colors">
                      {hasGallery ? <Ruler size={14} /> : <ArrowUpRight size={16} />}
                    </span>
                  </div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {p.surface && (
                      <span className="font-mono text-[10px] tracking-[0.22em] uppercase border border-[#F7F7F9]/40 text-[#F7F7F9] px-2 py-1">
                        {p.surface}
                      </span>
                    )}
                    {p.budget && (
                      <span className="font-mono text-[10px] tracking-[0.22em] uppercase bg-[#F7F7F9] text-[#0A0A0A] px-2 py-1">
                        {typeof p.budget === "string" ? p.budget : p.budget[lang]}
                      </span>
                    )}
                    {hasGallery && (
                      <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#D35400]">
                        {p.gallery.length} {lang === "fr" ? "documents" : "documents"}
                      </span>
                    )}
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>

      {openProject && <ProjectDetail project={openProject} onClose={() => setOpenId(null)} />}
    </section>
  );
}
